import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

/** Extract placeholder keys from template content. Supports [Candidate_Name] style (square brackets). */
export const extractPlaceholders = (content) => {
  if (!content || typeof content !== "string") return [];
  const keys = new Set();
  const re = /\[([^\]]+)\]/g;
  let m;
  while ((m = re.exec(content)) !== null) keys.add(m[1].trim());
  return Array.from(keys);
};

/** Normalize template from API (Id, annexure_content) to frontend shape (id, annexureContent). */
function normalizeTemplate(t) {
  if (!t) return t;
  return {
    ...t,
    id: t.id ?? t.Id ?? t.LetterTemplateId,
    name: t.name ?? t.TemplateName,
    type: t.type ?? t.LetterType,
    content: t.content ?? t.TemplateContent ?? "",
    annexureContent: t.annexureContent ?? t.annexure_content ?? t.AnnexureContent ?? "",
  };
}

const initialState = {
  list: [],
  selectedTemplateId: null,
  loading: false,
  generateLoading: false,
  error: null,
};

// GET /letterTemplate/LetterTemplateGetByCompanyId
export const getTemplatesByCompanyId = createAsyncThunk(
  "letterTemplate/getTemplatesByCompanyId",
  async (_arg, { rejectWithValue }) => {
    try {
      const { data } = await axios.get("letterTemplate/LetterTemplateGetByCompanyId");
      let raw =
        data?.list ??
        data?.data ??
        data?.letterTemplates ??
        data?.LetterTemplates ??
        data;
      if (!Array.isArray(raw)) raw = raw && (raw.Id != null || raw.id != null) ? [raw] : [];
      const list = Array.isArray(raw) ? raw : [];
      return list.map(normalizeTemplate);
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to load templates";
      toast.error(msg);
      return rejectWithValue(error.response?.data || { message: msg });
    }
  }
);

// GET /letterTemplate/LetterTemplateGetById?id=1
export const getTemplateById = createAsyncThunk(
  "letterTemplate/getTemplateById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `letterTemplate/LetterTemplateGetById?id=${encodeURIComponent(id)}`
      );
      const raw = data?.data ?? data ?? null;
      return raw ? normalizeTemplate(raw) : null;
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.message || "Failed to load template");
      }
      return rejectWithValue(error.response?.data || {});
    }
  }
);

// POST /letterTemplate/LetterTemplateSetup — one API for add (LetterTemplateId=0), edit (LetterTemplateId=id), delete (DataStatus=2)
// Backend params: LetterTemplateId, CompanyId, TemplateName, TemplateType, Content, AnnexureContent, DataStatus (1=active, 2=deleted)
export const saveTemplate = createAsyncThunk(
  "letterTemplate/saveTemplate",
  async (payload, { rejectWithValue }) => {
    try {
      const body = {
        LetterTemplateId: payload.id ?? payload.LetterTemplateId ?? 0,
        CompanyId: payload.companyId ?? 0,
        TemplateName: payload.name ?? "",
        TemplateType: payload.type ?? "offer",
        Content: payload.content ?? "",
        AnnexureContent: payload.annexureContent ?? "",
        DataStatus: 1,
      };
      const { data } = await axios.post("letterTemplate/LetterTemplateSetup", body);
      const saved = data?.data ?? data ?? body;
      toast.success(payload.id || payload.LetterTemplateId ? "Template updated" : "Template saved");
      return saved;
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to save template";
      toast.error(msg);
      return rejectWithValue(error.response?.data || { message: msg });
    }
  }
);

// Delete via same API: LetterTemplateSetup with DataStatus = 2
export const deleteTemplate = createAsyncThunk(
  "letterTemplate/deleteTemplate",
  async (payload, { rejectWithValue }) => {
    try {
      const id = payload?.id ?? payload?.LetterTemplateId ?? payload;
      const body = {
        LetterTemplateId: id,
        CompanyId: payload?.companyId ?? 0,
        TemplateName: "",
        TemplateType: "offer",
        Content: "",
        AnnexureContent: "",
        DataStatus: 2,
      };
      await axios.post("letterTemplate/LetterTemplateSetup", body);
      toast.success("Template deleted");
      return id;
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to delete template";
      toast.error(msg);
      return rejectWithValue(error.response?.data || { message: msg });
    }
  }
);

// POST /letterTemplate/LetterGenerate — body: templateId, placeholderValues; returns filled content and annexureContent
export const letterGenerate = createAsyncThunk(
  "letterTemplate/letterGenerate",
  async (
    { templateId, placeholderValues },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await axios.post("letterTemplate/LetterGenerate", {
        templateId,
        placeholderValues: placeholderValues ?? {},
      });
      return {
        content: data?.content ?? data?.data?.content ?? "",
        annexureContent: data?.annexureContent ?? data?.data?.annexureContent ?? "",
      };
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to generate letter";
      toast.error(msg);
      return rejectWithValue(error.response?.data || { message: msg });
    }
  }
);

const letterTemplateSlice = createSlice({
  name: "letterTemplate",
  initialState,
  reducers: {
    setSelectedTemplate(state, { payload }) {
      state.selectedTemplateId = payload;
    },
    deleteTemplateLocal(state, { payload }) {
      const id = payload?.id ?? payload;
      state.list = (state.list || []).filter(
        (t) => (t.id ?? t.Id ?? t.LetterTemplateId) !== id
      );
      if (state.selectedTemplateId === id) state.selectedTemplateId = null;
    },
    setError(state, { payload }) {
      state.error = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTemplatesByCompanyId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTemplatesByCompanyId.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list = Array.isArray(payload) ? payload : [];
        state.error = null;
      })
      .addCase(getTemplatesByCompanyId.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message || "Failed to load templates";
      })
      .addCase(getTemplateById.fulfilled, (state, { payload }) => {
        if (payload && (payload.id ?? payload.Id)) {
          const normalized = normalizeTemplate(payload);
          const idx = (state.list || []).findIndex(
            (t) => (t.id ?? t.Id) === (normalized.id ?? payload.Id)
          );
          if (idx >= 0) {
            state.list = [...state.list];
            state.list[idx] = { ...state.list[idx], ...normalized };
          } else {
            state.list = [...(state.list || []), normalized];
          }
        }
      })
      .addCase(saveTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveTemplate.fulfilled, (state, { payload }) => {
        state.loading = false;
        const normalized = normalizeTemplate(payload);
        const id = normalized.id ?? payload?.Id ?? payload?.LetterTemplateId;
        const idx = (state.list || []).findIndex(
          (t) => (t.id ?? t.Id) === id
        );
        if (idx >= 0) {
          state.list = [...state.list];
          state.list[idx] = { ...state.list[idx], ...normalized };
        } else {
          state.list = [...(state.list || []), normalized];
        }
      })
      .addCase(saveTemplate.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deleteTemplate.fulfilled, (state, { payload }) => {
        const id = payload;
        state.list = (state.list || []).filter(
          (t) => (t.id ?? t.Id ?? t.LetterTemplateId) !== id
        );
        if (state.selectedTemplateId === id) state.selectedTemplateId = null;
      })
      .addCase(letterGenerate.pending, (state) => {
        state.generateLoading = true;
      })
      .addCase(letterGenerate.fulfilled, (state) => {
        state.generateLoading = false;
      })
      .addCase(letterGenerate.rejected, (state) => {
        state.generateLoading = false;
      });
  },
});

export const {
  setSelectedTemplate,
  deleteTemplateLocal,
  setError,
} = letterTemplateSlice.actions;
// deleteTemplate (async) is the preferred way to delete; deleteTemplateLocal only updates state without API

export default letterTemplateSlice.reducer;
